import React, { useRef, useState, type JSX } from 'react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment-timezone';
import { Box, Button, Divider, FormControl, MenuItem, Paper, Radio, Select, Stack } from '@mui/material';

import { WidgetFooter } from 'pages/WidgetManagement/common/WidgetFooter';
import { WidgetHeader } from 'pages/WidgetManagement/common/WidgetHeader';
import { MultiColumnList } from 'components/MultiColumnList';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Download from '@mui/icons-material/Download';
import { useIsMount, useNewCustomTranslations, useRBAC } from 'hooks';
import { StyledMenu } from 'pages/Chart/StyledMenu';
import { getRandomColor, getTimeInFromTo } from '@fiji/common/src/utils/helpers';
import { useTransformData } from 'hooks/useTransformData';
import { seriesColors, useTypedSelector } from '@fiji/common';
import { useDispatch } from 'react-redux';
import { setTrendsLegendData } from '@fiji/common/src/features/widgetManagement/trendsWidgetSlice';
import { useGetChannelUnitsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { CustomTransComponent } from 'components';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export type TelemetryChartProps = {
    type?: 'area' | 'line';
    widgetTypeId?: string;
    route?: string;
    state?: string;
    maxCount: number;
    secondary: any[];
    widgetName: string;
    [key: string]: any;
};

// Preview/configured containers (e.g. the widget bar) don't always give the widget a definite
// height to inherit, and the chart's own height is derived from this container's rect minus a
// fixed offset (header + legend list). Giving the container a taller fixed height in those modes
// grows only the chart (MultiColumnList below has content-based height and stays as-is).
const PREVIEW_CONTAINER_HEIGHT = 800;
const isPreviewLikeMode = (mode?: string): boolean => mode === 'preview' || mode === 'configured';

const Component = React.forwardRef(
    (
        {
            widgetData,
            mode,
            payloadChangeHandler,
            downloadHandler,
            handlerLegendsPayload,
        }: {
            widgetData: TelemetryChartProps;
            mode?: string;

            payloadChangeHandler: any;
            downloadHandler: any;
            handlerLegendsPayload: any;
        },
        ref
    ): any => {
        const dispatch = useDispatch();
        const trendsLegendData: any = useTypedSelector(
            (state) => state.trends.trendsLegendData[widgetData?.widgetName]
        );
        const { pointConversion, timezone } = useTransformData();
        const componentRef: any = useRef(null);
        const [chartHeight, setChartHeight] = useState<any>(null);
        const isFirstRender = useRef(true);

        const isMount = useIsMount();
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

        const chartRef: any = useRef(null);

        const [channelIds, setChannelIds] = useState<any>([]);
        const [activeDuration, setActiveDuration] = useState<number>(widgetData?.duration ?? 24);
        const [selectedUnits, setSelectedUnits] = React.useState<any>(widgetData?.selectedUnit ?? '');
        const [isRefreshed, setIsRefreshed] = React.useState(false);
        const open = Boolean(anchorEl);
        const permissions = useTypedSelector(selectCurrentPermission);
        const { hasPermission } = useRBAC(permissions);
        const canExportSummary = hasPermission('trends-summary-export-trends_create-devices');
        const canExportSummaryExpired = hasPermission('trends-summary-export-trends_create-devices_hasExpired');
        const canViewTrendDetails = hasPermission('trends-details_view-devices');
        const canViewTrendDetailsExpired = hasPermission('trends-details_view-devices_hasExpired');
        useNewCustomTranslations('MULTI_TRENDS');
        const [options, setOptions] = useState<any>({ xAxis: { type: 'time' }, yAxis: { type: 'value' }, series: [] });
        const tabsData = useTypedSelector((state: any) => state.common.tabsList);

        const unitSuffixMap = useRef<Record<string, string>>({});
        const yAxisUnitRef = useRef<string>('');

        const { data: unitList } = useGetChannelUnitsQuery(getPayload(), {
            skip: !getPayload()?.length || mode !== 'view',
        });

        const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = (): void => {
            setAnchorEl(null);
        };

        React.useEffect(() => {
            if (!isMount) {
                setIsRefreshed(false);
                if (!isRefreshed)
                    payloadChangeHandler?.({
                        payload: channelIds,
                        activeDuration: getTimeInFromTo(activeDuration, 'h'),
                    });
            }
        }, [activeDuration]);

        React.useEffect(() => {
            if (!isMount && channelIds) {
                setIsRefreshed(false);
                if (!isRefreshed)
                    handlerLegendsPayload?.({
                        payload: channelIds,
                        activeDuration: getTimeInFromTo(activeDuration, 'h'),
                    });
            }
        }, [channelIds]);

        function getPayload(): any {
            const groupedPayload = new Map<any, any>();

            widgetData?.secondary?.forEach((item: any) => {
                const existing = groupedPayload.get(item.deviceId);
                if (existing) {
                    existing.channelId.push(item.channelId);
                    return;
                }

                groupedPayload.set(item.deviceId, { deviceId: item.deviceId, channelId: [item.channelId] });
            });

            return Array.from(groupedPayload.values());
        }

        const getSeriesData = (data: any): any[] => {
            let maxDataValue = -Infinity;
            let maxSeriesUnit = '';

            const series = data?.map((trend: any, index: number) => {
                const seriesId = `${trend?.deviceId}-${trend?.channelId}`;
                const suffix = trend?.unit ? ` ${trend?.unit}` : '';
                unitSuffixMap.current[seriesId] = suffix;

                // Find series with the highest data value (replicates old getUnit logic)
                const seriesMax = trend?.data?.reduce(
                    (max: number, point: any) => (point?.y > max ? point?.y : max),
                    -Infinity
                );
                if (seriesMax > maxDataValue) {
                    maxDataValue = seriesMax;
                    maxSeriesUnit = suffix;
                }

                const color =
                    trendsLegendData?.legendData?.find?.(
                        (item: any) => `${trend?.deviceId}_${trend?.channelId}` === item?.uId
                    )?.color ?? seriesColors[index];
                return {
                    id: seriesId,
                    name: trend?.displayName,
                    // Shift x into the target timezone so the axis (rendered with useUTC) and the
                    // tooltip both display the same wall-clock time.
                    data:
                        trend?.data?.map((point: any): any => [
                            point?.x + (timezone ? moment.tz(point?.x, timezone).utcOffset() * 60000 : 0),
                            point?.y,
                        ]) ?? [],
                    type: widgetData?.type === 'area' ? 'line' : widgetData?.type ?? 'line',
                    ...(widgetData?.type === 'area' && {
                        areaStyle: { opacity: 0.7 },
                    }),
                    lineStyle: { color },
                    itemStyle: { color },
                    showSymbol: false,
                    emphasis: { focus: 'series' },
                };
            });

            yAxisUnitRef.current = maxSeriesUnit;
            return series ?? [];
        };

        const getYAxis = (): any => {
            const splitNumber = chartHeight ? Math.max(2, Math.floor(chartHeight / 60)) : 5;
            return {
                type: 'value',
                name: '',
                splitNumber,
                axisLabel: {
                    formatter: (value: number): string => `${pointConversion(value)}${yAxisUnitRef.current}`,
                },
                splitLine: {
                    lineStyle: { type: 'solid' },
                },
                // `tooltip.trigger: 'axis'` shows an axisPointer on every axis in the grid by
                // default, which is why hovering drew both a vertical (x) AND horizontal (y)
                // dashed line. Only the vertical line (time position) is wanted, so hide the
                // y-axis's own pointer here and leave the x-axis's default pointer as-is.
                axisPointer: { show: false },
            };
        };

        const addPoints = (message: any): void => {
            const instance = chartRef?.current?.getEchartsInstance();
            if (!instance) return;
            const currentOption = instance.getOption();
            const seriesArr = currentOption?.series as any[];
            if (!seriesArr?.length) return;
            const seriesId = `${message?.deviceId}-${message?.channelId}`;
            const seriesIndex = seriesArr.findIndex((s: any) => s.id === seriesId);
            if (seriesIndex === -1) return;
            const shiftedTimestamp =
                message.timestamp + (timezone ? moment.tz(message.timestamp, timezone).utcOffset() * 60000 : 0);
            const newData = [...(seriesArr[seriesIndex].data || []), [shiftedTimestamp, +message.value]];
            seriesArr[seriesIndex] = { ...seriesArr[seriesIndex], data: newData };
            if (message?.unit) {
                unitSuffixMap.current[seriesId] = ` ${message?.unit}`;
            }

            // Recompute the y-axis unit suffix from the updated series data so it stays in sync
            // with the series holding the highest value (mirrors the logic in getSeriesData).
            let maxDataValue = -Infinity;
            let maxSeriesUnit = '';
            seriesArr.forEach((s: any) => {
                const seriesMax = (s?.data ?? []).reduce(
                    (max: number, point: any) => (point?.[1] > max ? point?.[1] : max),
                    -Infinity
                );
                if (seriesMax > maxDataValue) {
                    maxDataValue = seriesMax;
                    maxSeriesUnit = unitSuffixMap.current[s?.id] ?? '';
                }
            });
            yAxisUnitRef.current = maxSeriesUnit;

            instance.setOption({ series: seriesArr, yAxis: getYAxis() });
            instance.hideLoading();
        };

        React.useImperativeHandle(
            ref,
            () => ({
                addPoints: (message: any): void => addPoints(message),
            }),
            [widgetData]
        );

        React.useEffect(() => {
            if (
                !trendsLegendData ||
                (widgetData?.channelData &&
                    trendsLegendData?.legendData?.length !==
                        widgetData?.channelData?.slice(0, widgetData?.maxCount ?? widgetData?.secondary?.length)
                            ?.length)
            ) {
                setIsRefreshed(true);
                const newColors = JSON.parse(JSON.stringify(seriesColors));

                const legendDataN = widgetData?.channelData
                    ?.slice(0, widgetData?.maxCount ?? widgetData?.secondary?.length)
                    ?.map((item: any, index: number) => {
                        let color: string = getRandomColor();
                        if (index < widgetData?.secondary?.length) {
                            color = newColors[0];
                            newColors.shift();
                        }
                        return {
                            channelId: item?.id ?? item?.channelId,
                            id: item?.channelId,
                            uId: `${item?.deviceId}_${item?.channelId}`,
                            deviceId: item?.deviceId,
                            color: color,
                            visible: Boolean(index < widgetData?.secondary?.length),
                            name: item?.displayName,
                        };
                    });
                dispatch(
                    setTrendsLegendData({
                        id: widgetData?.widgetName,
                        value: {
                            legendData: legendDataN,
                            colors: newColors,
                        },
                    })
                );
                setChannelIds(
                    widgetData?.channelData?.filter(
                        (channels: any) =>
                            legendDataN?.find((item: any) => item?.channelId === (channels?.id ?? channels?.channelId))
                                ?.visible
                    )
                );
            }
        }, [widgetData?.channelData]);

        const getFilter = (): JSX.Element => (
            <Box display={'flex'} alignItems={'center'} position={'absolute'} right={0}>
                <FormControl sx={{ minWidth: 80 }}>
                    <Select
                        disabled={mode !== 'view'}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="Age"
                        variant="standard"
                        disableUnderline
                        defaultValue={widgetData?.duration ?? 24}
                        onChange={(e: any): void => setActiveDuration(e?.target?.value)}
                    >
                        <MenuItem value={24}>
                            <CustomTransComponent translationKey={'MULTI_TRENDS:TWENTY_FOUR_HOURS'} />
                        </MenuItem>
                        <MenuItem value={360}>
                            <CustomTransComponent translationKey={'MULTI_TRENDS:FIFTEEN_DAYS'} />
                        </MenuItem>
                        <MenuItem value={744}>
                            <CustomTransComponent translationKey={'MULTI_TRENDS:THIRTY_ONE_DAYS'} />
                        </MenuItem>
                    </Select>
                </FormControl>
                {(canExportSummary || canExportSummaryExpired) && (
                    <Button
                        disabled={mode !== 'view'}
                        startIcon={
                            <Download
                                sx={{
                                    color: mode !== 'view' ? '#b2b2b8' : '#727e84',
                                }}
                            />
                        }
                        onClick={(): void => downloadHandler?.()}
                        className="download-icon"
                    />
                )}
            </Box>
        );

        React.useEffect(() => {
            const instance = chartRef?.current?.getEchartsInstance();
            let data: any = JSON.parse(JSON.stringify(widgetData?.secondary ?? []));

            if (selectedUnits) {
                data = data?.filter((trend: any) => trend?.unit === selectedUnits);
            }
            if (
                widgetData?.secondary?.length &&
                data?.some((item: any) => Boolean(item?.data?.length)) &&
                trendsLegendData?.legendData?.length
            ) {
                const seriesData = getSeriesData(
                    data?.filter(
                        (trend: any) =>
                            trendsLegendData?.legendData?.find?.(
                                (item: any) => `${trend?.deviceId}_${trend?.channelId}` === item?.uId
                            )?.visible
                    )
                );
                const formatTooltipHeader = (value: number | string): string =>
                    // value is already shifted into the target timezone, so format as UTC.
                    moment.utc(value).format('dddd, DD MMM, HH:mm:ss');
                // The axis must span the whole selected tab's window (e.g. 15 days), not just
                // whatever range the device actually reported data for — otherwise a device with
                // gaps/sparse reporting shrinks the axis down to only its earliest/latest points.
                const { from: rangeFrom, to: rangeTo } = getTimeInFromTo(activeDuration, 'h');
                const shiftToTimezone = (timestamp: number): number =>
                    timestamp + (timezone ? moment.tz(timestamp, timezone).utcOffset() * 60000 : 0);
                const newOption: any = {
                    useUTC: true,
                    grid: {
                        left: 60,
                        right: 30,
                        top: 20,
                        bottom: 50,
                        containLabel: false,
                    },
                    xAxis: {
                        type: 'time',
                        min: shiftToTimezone(rangeFrom),
                        max: shiftToTimezone(rangeTo),
                        axisLabel: {
                            // Rendered in UTC (data is pre-shifted to target tz): hour ticks show the
                            // time, day-boundary ticks show the date so times are anchored to their date.
                            formatter: {
                                hour: '{HH}:{mm}',
                                day: '{dd} {MMM}',
                                month: '{dd} {MMM}',
                                year: '{yyyy}',
                            },
                        },
                    },
                    yAxis: getYAxis(),
                    series: seriesData,
                    tooltip: {
                        trigger: 'axis',
                        formatter: (params: any): string => {
                            if (!params?.length) return '';
                            const header = formatTooltipHeader(params[0]?.value?.[0]);
                            let content = `${header}`;
                            params.forEach((p: any) => {
                                const suffix = unitSuffixMap.current[p.seriesId] ?? '';
                                content += `<br/>${p.marker} ${p.seriesName}: <b>${pointConversion(
                                    p.value[1]
                                )}${suffix}</b>`;
                            });
                            return content;
                        },
                    },
                    legend: { show: false },
                    dataZoom: [
                        {
                            type: 'inside',
                            xAxisIndex: 0,
                            filterMode: 'none',
                            zoomOnMouseWheel: true,
                            moveOnMouseMove: true,
                            moveOnMouseWheel: false,
                        },
                    ],
                };
                setOptions(newOption);
                instance?.hideLoading();
            } else {
                setOptions((prev: any) => ({ ...prev, series: [] }));
            }
        }, [widgetData, selectedUnits, trendsLegendData, chartHeight, timezone, activeDuration]);

        const clearTrendChartSeries = (): void => {
            const instance = chartRef?.current?.getEchartsInstance();
            if (instance) {
                instance.setOption({ series: [] }, true);
            }
        };

        const changeHandler = (e: any, id: number): void => {
            const newLegendData = JSON.parse(JSON.stringify(trendsLegendData?.legendData ?? []));
            const newColors = JSON.parse(JSON.stringify(trendsLegendData?.colors ?? []));
            const index = newLegendData?.findIndex((legend: any): any => legend.channelId === id);

            if (index > -1) {
                newLegendData[index].visible = e.target.checked;
                if (e.target.checked) {
                    newLegendData[index].color = newColors[0];
                    newColors?.shift();
                } else {
                    newColors.unshift(newLegendData[index].color);
                }
            }

            setChannelIds(
                widgetData?.channelData?.filter(
                    (channels: any) =>
                        newLegendData?.find((item: any) => item?.channelId === (channels?.id ?? channels?.channelId))
                            ?.visible
                )
            );
            dispatch(
                setTrendsLegendData({
                    id: widgetData?.widgetName,
                    value: {
                        legendData: newLegendData,
                        colors: newColors,
                    },
                })
            );
            clearTrendChartSeries();
        };

        const observerRef = useRef<ResizeObserver | null>(null);

        const handleResizeComponent = (): void => {
            if (isFirstRender.current && mode === 'configured') {
                isFirstRender.current = false; // Skip the first trigger
                return;
            }
            if (!componentRef.current) return;
            const hightWidth = componentRef.current.getElementsByClassName('d-flex')?.[0]?.getBoundingClientRect();
            setChartHeight(
                Math.floor((componentRef.current.getBoundingClientRect()?.height ?? 0) - (hightWidth?.height ?? 0) - 75)
            );
        };

        const setComponentRef = React.useCallback(
            (node: HTMLDivElement | null) => {
                // Cleanup previous observer
                if (observerRef.current) {
                    observerRef.current.disconnect();
                    window.removeEventListener('resize', handleResizeComponent);
                }
                componentRef.current = node;

                if (node && trendsLegendData?.legendData?.length) {
                    observerRef.current = new ResizeObserver(handleResizeComponent);
                    observerRef.current.observe(node);
                    window.addEventListener('resize', handleResizeComponent);
                    handleResizeComponent();
                }
            },
            [trendsLegendData?.legendData?.length]
        );

        React.useEffect(
            () => () => {
                if (observerRef.current) {
                    observerRef.current.disconnect();
                }
                window.removeEventListener('resize', handleResizeComponent);
            },
            []
        );

        return (
            <Paper sx={{ height: isPreviewLikeMode(mode) ? PREVIEW_CONTAINER_HEIGHT : '100%' }}>
                <WidgetHeader mode={mode} widgetData={widgetData} headerAction={getFilter()} />
                {mode !== 'edit' && <Divider />}
                <Stack
                    sx={{
                        height: `calc(100% - 97px)`,
                        overflow: 'auto',
                        justifyContent: 'space-between',
                    }}
                    ref={setComponentRef}
                >
                    <Stack
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                        alignItems={'flex-end'}
                        p={2}
                        pr={5}
                    >
                        <Box mr={2}>
                            <Button
                                disabled={mode !== 'view'}
                                id="demo-customized-button"
                                aria-controls={open ? 'demo-customized-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                sx={{
                                    backgroundColor: 'transparent',
                                    color: '#424E54',
                                }}
                                onClick={handleClick}
                                endIcon={!open ? <ArrowDropDown /> : <ArrowDropUp />}
                            >
                                <CustomTransComponent translationKey={'COMMON:UNITS'} />
                            </Button>
                            <StyledMenu
                                id="demo-customized-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'demo-customized-button',
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                            >
                                {unitList?.data?.map((item: any): any => (
                                    <MenuItem key={item} value={item} className="w-100">
                                        <Radio
                                            className="margin-right-16"
                                            checked={selectedUnits === item}
                                            onChange={(): void => setSelectedUnits(item)}
                                        />
                                        {item}
                                    </MenuItem>
                                ))}
                            </StyledMenu>
                        </Box>
                    </Stack>
                    <ReactECharts
                        ref={chartRef}
                        option={options}
                        notMerge={true}
                        style={{ height: chartHeight ? `${chartHeight}px` : '300px', width: '100%' }}
                    />
                    <MultiColumnList
                        isDisabled={mode !== 'view'}
                        isLoading={false}
                        legendData={trendsLegendData?.legendData ?? []}
                        changeHandler={changeHandler}
                        maxItemsPerColumn={5}
                    />
                </Stack>
                {(canViewTrendDetails || tabsData?.includes('trends') || canViewTrendDetailsExpired) && (
                    <WidgetFooter
                        mode={mode ?? ''}
                        ids={[]}
                        widgetData={{ ...widgetData, viewAllButton: true }}
                        actionBtnName={<CustomTransComponent translationKey={'COMMON:VIEW_FULL_TREND_VIEWER'} />}
                    />
                )}
            </Paper>
        );
    }
);
export default Component;
